import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiTrash2, FiPlus, FiEdit2 } from "react-icons/fi";
import { FaTimes } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { selectCategories } from "../store/slices/products/slice";
import {
  createCategory,
  deleteCategory,
  getCategories,
} from "../store/slices/products/asyncThunks";
import productsService from "../api/services/products";
import AdminContainer from "../components/AdminContainer";
import AdminTitle from "../components/utils/AdminTitle";
import AdminToast from "../components/utils/AdminToast";

const CategoryPage = () => {
  const categories = useSelector(selectCategories);
  const dispatch = useDispatch();
  const [toast, setToast] = useState(null);
  const [deleteCategoryModal, setDeleteCategoryModal] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [categoryTitle, setCategoryTitle] = useState("");
  const categoryRef = useRef(null);

  const [editCategory, setEditCategory] = useState(null);
  const [editName, setEditName] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const handleUpdateCategory = () => {
    if (editLoading) return;
    if (!editName.trim()) {
      setToast({ type: "error", message: "Naziv kategorije ne sme biti prazan." });
      return;
    }
    setEditLoading(true);
    productsService
      .updateCategory(editCategory.id, { name: editName.trim() })
      .then(() => {
        setEditCategory(null);
        setToast({ type: "success", message: "Kategorija je izmenjena." });
        dispatch(getCategories());
      })
      .catch((err) =>
        setToast({ type: "error", message: err.message || "Greška pri izmeni." })
      )
      .finally(() => setEditLoading(false));
  };

  const handleCreateCategory = () => {
    if (!categoryTitle.trim()) {
      setToast({ type: "error", message: "Unesi naziv kategorije." });
      categoryRef.current?.focus();
      return;
    }
    if (categories.createLoading) return;

    dispatch(createCategory({ name: categoryTitle.trim() }))
      .unwrap()
      .then(() => {
        setCategoryTitle("");
        setToast({ type: "success", message: "Kategorija je kreirana." });
      })
      .catch((err) =>
        setToast({
          type: "error",
          message: err.message || "Greška pri kreiranju kategorije.",
        })
      );
  };

  useEffect(() => {
    if (deleteCategoryModal) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
  }, [deleteCategoryModal]);

  return (
    <AdminContainer>
      {toast && (
        <AdminToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <AdminTitle kanji="類">Kategorije</AdminTitle>

      <CreateRow>
        <Input
          ref={categoryRef}
          placeholder="Naziv nove kategorije…"
          value={categoryTitle}
          onChange={(e) => setCategoryTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleCreateCategory();
          }}
        />
        <ButtonAdd
          $disabled={!categoryTitle.trim() || categories.createLoading}
          onClick={handleCreateCategory}
        >
          {categories.createLoading ? (
            <l-line-spinner size="14" stroke="2" speed="1" color="#fe5f55" />
          ) : (
            <FiPlus size={16} />
          )}
          Dodaj
        </ButtonAdd>
      </CreateRow>

      {categories.loading ? (
        <l-line-spinner size="32" stroke="3" speed="1" color="#fe5f55" />
      ) : (
        <Grid>
          {categories.data.map((ctg) => (
            <Card key={ctg.id}>
              <span className="kj">類</span>
              <b>{ctg.name}</b>
              <IconBtn
                title="Izmeni kategoriju"
                onClick={() => {
                  setEditCategory(ctg);
                  setEditName(ctg.name);
                }}
              >
                <FiEdit2 size={15} />
              </IconBtn>
              <IconBtn
                title="Obriši kategoriju"
                onClick={() => {
                  setCategoryId(ctg.id);
                  setCategoryName(ctg.name);
                  setDeleteCategoryModal(true);
                }}
              >
                <FiTrash2 size={16} />
              </IconBtn>
            </Card>
          ))}
        </Grid>
      )}

      <AnimatePresence>
        {editCategory && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !editLoading && setEditCategory(null)}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn onClick={() => !editLoading && setEditCategory(null)}>
                <FaTimes />
              </CloseBtn>
              <h3>Izmeni kategoriju</h3>
              <ModalInput
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdateCategory();
                }}
                placeholder="Naziv kategorije"
                autoFocus
              />
              <ModalActions>
                <Cancel onClick={() => !editLoading && setEditCategory(null)}>
                  Odustani
                </Cancel>
                <Confirm
                  $disabled={editLoading || !editName.trim()}
                  onClick={handleUpdateCategory}
                >
                  {editLoading ? "Čuvanje…" : "Sačuvaj"}
                </Confirm>
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteCategoryModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              if (!categories.deleteLoading) {
                setDeleteCategoryModal(false);
              }
            }}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn
                onClick={() => {
                  if (!categories.deleteLoading) {
                    setDeleteCategoryModal(false);
                  }
                }}
              >
                <FaTimes />
              </CloseBtn>
              <h3>Obriši kategoriju</h3>
              <p>
                Sigurno brišeš kategoriju <b>{categoryName}</b>? Ova akcija ne
                može da se poništi.
              </p>
              <ModalActions>
                <Cancel
                  onClick={() => {
                    if (!categories.deleteLoading) {
                      setDeleteCategoryModal(false);
                    }
                  }}
                >
                  Odustani
                </Cancel>
                {!categories.deleteLoading ? (
                  <Confirm
                    onClick={() => {
                      dispatch(deleteCategory(categoryId))
                        .unwrap()
                        .then(() => {
                          setDeleteCategoryModal(false);
                          setToast({
                            type: "success",
                            message: "Kategorija je obrisana.",
                          });
                        })
                        .catch((err) =>
                          setToast({
                            type: "error",
                            message: err.message || "Greška pri brisanju.",
                          })
                        );
                    }}
                  >
                    Obriši
                  </Confirm>
                ) : (
                  <Confirm $disabled>
                    Obriši
                    <l-line-spinner
                      size="14"
                      stroke="2"
                      speed="1"
                      color="#fff"
                    />
                  </Confirm>
                )}
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </AdminContainer>
  );
};

export default CategoryPage;

const CreateRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;
  max-width: 52rem;
  margin-bottom: 1.4rem;
`;

const Input = styled.input`
  flex: 1;
  min-width: 22rem;
  padding: 0 1.4rem;
  height: 4.4rem;
  background: var(--bg1, #101013);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: var(--text, #f4f2ee);
  border-radius: 0.4rem;
  font-family: "Poppins-Regular";
  font-size: 1.3rem;
  outline: none;
  transition: border-color 0.25s;

  &::placeholder {
    color: var(--muted2, #5c5a63);
  }

  &:focus {
    border-color: var(--red, #fe5f55);
  }
`;

const ButtonAdd = styled.button`
  height: 4.4rem;
  padding: 0 2rem;
  background: ${(props) =>
    props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red, #fe5f55)"};
  color: ${(props) =>
    props.$disabled ? "var(--muted, #8e8c94)" : "var(--bg0, #0a0a0c)"};
  border: 1px solid
    ${(props) =>
      props.$disabled ? "var(--line2, rgba(255,255,255,0.14))" : "transparent"};
  border-radius: 0.4rem;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.2rem;
  transition: background 0.25s var(--ease, ease);

  &:hover {
    background: ${(props) =>
      props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red-deep, #e04a40)"};
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(24rem, 1fr));
  gap: 1.2rem;
  width: 100%;
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  background: var(--bg1, #101013);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.4rem;
  padding: 1.4rem 1.6rem;
  transition: border-color 0.25s;

  &:hover {
    border-color: var(--line2, rgba(255, 255, 255, 0.14));
  }

  .kj {
    font-family: var(--font-kanji);
    font-size: 1.8rem;
    color: rgba(254, 95, 85, 0.5);
  }

  b {
    flex: 1;
    font-family: "Poppins-SemiBold";
    font-size: 1.4rem;
    color: var(--text, #f4f2ee);
  }
`;

const IconBtn = styled.button`
  width: 3.4rem;
  height: 3.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 0.4rem;
  color: var(--muted, #8e8c94);
  cursor: pointer;
  transition: all 0.25s;

  &:hover {
    color: var(--red, #fe5f55);
    border-color: var(--red, #fe5f55);
  }
`;

const ModalInput = styled.input`
  width: 100%;
  height: 4.4rem;
  padding: 0 1.4rem;
  margin-bottom: 2rem;
  background: var(--bg0, #0a0a0c);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: var(--text, #f4f2ee);
  border-radius: 0.4rem;
  font-family: "Poppins-Regular";
  font-size: 1.4rem;
  outline: none;
  transition: border-color 0.25s;

  &::placeholder {
    color: var(--muted2, #5c5a63);
  }

  &:focus {
    border-color: var(--red, #fe5f55);
  }
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(6, 6, 8, 0.8);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 1.4rem;
  right: 1.4rem;
  background: var(--bg0, #0a0a0c);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: #fff;
  border-radius: 50%;
  font-size: 1.3rem;
  width: 3.2rem;
  height: 3.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s;

  &:hover {
    background: var(--red, #fe5f55);
    border-color: var(--red, #fe5f55);
    color: var(--bg0, #0a0a0c);
  }
`;

const ConfirmBox = styled.div`
  background: var(--bg2, #17171c);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  padding: 3rem 2.4rem;
  border-radius: 0.6rem;
  max-width: 42rem;
  width: 100%;
  color: var(--text, #f4f2ee);
  position: relative;
  text-align: center;

  h3 {
    font-family: "Anton", sans-serif;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 2rem;
    margin-bottom: 1.2rem;
    color: var(--text, #f4f2ee);
  }

  p {
    font-family: "Poppins-Regular";
    font-size: 1.35rem;
    line-height: 1.6;
    margin-bottom: 2.2rem;
    color: var(--muted, #8e8c94);

    b {
      font-family: "Poppins-SemiBold";
      color: var(--red, #fe5f55);
    }
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const Cancel = styled.button`
  padding: 1.1rem 2.2rem;
  border-radius: 0.4rem;
  background: transparent;
  color: var(--muted, #8e8c94);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.25s;

  &:hover {
    color: var(--text, #f4f2ee);
    border-color: var(--text, #f4f2ee);
  }
`;

const Confirm = styled.button`
  padding: 1.1rem 2.2rem;
  border-radius: 0.4rem;
  background: var(--red, #fe5f55);
  color: var(--bg0, #0a0a0c);
  border: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.2rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: background 0.25s;

  &:hover {
    background: var(--red-deep, #e04a40);
  }
`;
